import { type Level, levelOf, renderStepText } from "@typedoc/contracts/processes";
import type { DraftStep, LiveDraft } from "./live-draft";
import type { Names } from "./people";

/**
 * What the canvas's steps list shows of a live draft (Paper p-15-0, SV1, SV2 and SV7; decision
 * `2026-10-31-steps-view-decided-as-a-whole`). Pure: the components draw what these say.
 *
 * - Each step is a row: its number in SOP order, its level, its outcome with Each and Only when as
 *   muted clauses, and its Done when.
 * - The level says who does the work (`levelOf`, from contracts): the level Abacor set from the SOP,
 *   or, without one, Abacor's with a human confirming for a step that changes or sends something,
 *   and Abacor's for the rest. A Who written as text before levels stands for a person.
 * - Text is shown with its slots' values in place of their `{{key}}` tokens until the chips land.
 * - After the first publish, a step that differs from the live version is marked: what the next
 *   publish would change.
 */

export { type Level, levelOf };

export const LEVELS: readonly Level[] = ["abacor", "confirms", "human"];

/** What each level says, on its own and in the summary under the title. */
export const LEVEL_LABEL: Record<Level, { name: string; count: string }> = {
  abacor: { name: "Abacor does it", count: "by Abacor" },
  confirms: { name: "Abacor does it, a human confirms", count: "a human confirms" },
  human: { name: "A human does it", count: "by a human" },
};

export type StepRow = {
  stepId: string;
  /** Its place in the draft, two digits: "01". */
  number: string;
  level: Level;
  title: string;
  /** Each and Only when, as the muted clauses after the title, in that order. */
  clauses: readonly string[];
  doneWhen: string | null;
  /** The draft differs from the live version here: what the next publish would change. */
  changedSinceLive: boolean;
};

/** A place in the list as people read it: two digits, as SOP steps are numbered. */
export const stepNumber = (index: number) => String(index + 1).padStart(2, "0");

/** A clause read after the title: "for every line", "when a line is short". */
/** The words a clause opens with that read on in lower case after the title; a name stays as written. */
const READS_ON = /^(for|when|if|each|every|all|any|a|an|the|only|its|it|there)\b/i;

function clause(prefix: string, value: string | undefined): string | null {
  if (!value) return null;
  // Written as a sentence ("Each item on the purchase order."), it still reads on from the title.
  const said = value.trim().replace(/[.\s]+$/, "");
  if (!said) return null;
  const text = READS_ON.test(said) ? said.charAt(0).toLowerCase() + said.slice(1) : said;
  // Abacor often writes the clause whole ("for every line"); a bare value takes the word.
  return text.toLowerCase().startsWith(`${prefix} `) ? text : `${prefix} ${text}`;
}

/** A step's Each and Only when as the clauses read after its title; null where it has none. */
export function stepClauses(
  step: DraftStep,
  names?: Names,
): { each: string | null; onlyWhen: string | null } {
  const { body } = step;
  const said = (text: string | undefined) =>
    text === undefined ? undefined : renderStepText(body, text, names);
  return {
    each: clause("for", said(body.answers.each?.value)),
    onlyWhen: clause("when", said(body.answers.onlyWhen?.value)),
  };
}

/** A row of the list. `names` says who a member slot is; without it, they read as "a member". */
export function stepRow(step: DraftStep, index: number, live: boolean, names?: Names): StepRow {
  const { body } = step;
  const said = (text: string | undefined) =>
    text === undefined ? undefined : renderStepText(body, text, names);
  const { each, onlyWhen } = stepClauses(step, names);
  return {
    stepId: step.stepId,
    number: stepNumber(index),
    level: levelOf(body),
    title: renderStepText(body, body.title.value, names),
    clauses: [each, onlyWhen].filter((text): text is string => text !== null),
    doneWhen: said(body.answers.doneWhen?.value) ?? null,
    changedSinceLive: live && step.hash !== step.baseHash,
  };
}

export function stepRows(draft: LiveDraft, names?: Names): StepRow[] {
  const live = draft.draft.baseReleaseId !== null;
  return draft.steps.map((step, index) => stepRow(step, index, live, names));
}

/** How many steps each level has, in the order the summary lists them; levels with none are left out. */
export function levelCounts(rows: readonly StepRow[]): { level: Level; count: number }[] {
  return LEVELS.map((level) => ({
    level,
    count: rows.filter((row) => row.level === level).length,
  })).filter(({ count }) => count > 0);
}

/**
 * Where the process stands, for the line under its name (SV7): a draft never published, or the
 * live version and whether the draft has changed since. `changes` counts the steps that differ
 * from live and the facts if they do; a step removed since live is not counted until the draft
 * says which ones were.
 */
export type Standing =
  | { kind: "draft" }
  | { kind: "live"; changes: number }
  | { kind: "closed"; status: LiveDraft["draft"]["status"] };

export function standingOf(draft: LiveDraft, rows: readonly StepRow[]): Standing {
  if (draft.draft.status !== "open") return { kind: "closed", status: draft.draft.status };
  if (draft.draft.baseReleaseId === null) return { kind: "draft" };
  const facts = draft.draft.factsHash !== draft.draft.factsBaseHash ? 1 : 0;
  return { kind: "live", changes: rows.filter((row) => row.changedSinceLive).length + facts };
}

/** "runs once for each purchase order", from the case unit; null before it is written. */
export function runsOnceFor(draft: LiveDraft): string | null {
  const unit = draft.draft.facts.caseUnit?.value.trim();
  if (!unit) return null;
  const bare = unit.replace(/^(a|an|one|each)\s+/i, "");
  return `runs once for each ${bare}`;
}

/**
 * Whether the trigger starts cases by itself: the switch on the Starts when row (decision
 * `2026-11-01-publishing-is-a-deploy-and-on-is-the-gate`, item 3). No switch is stored yet, so
 * every playbook is Off; this is the one place that reads it once one is.
 */
export function isOn(draft: LiveDraft): boolean {
  void draft;
  return false;
}

/** "v2 published", or "Published" when the version could not be read. */
export const publishedName = (version: number | null) =>
  version === null ? "Published" : `v${String(version)} published`;
